import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import isEmpty from "lodash/isEmpty";
import BackButton from "components/button/ExportButton";
import ButtonComponent from "components/button/ButtonComponent";
import Notify from "config/notify";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import TableCarTireByCode from "../components/TableCarTireByCode";
import { ITireItemDetail } from "../types";
import useProductCode from "../hook/useProductCode";

const styles = {
  title: {
    fontSize: { sm: "18px", md: "20px", xl: "25px" },
    fontWeight: "medium",
    color: "var(--gray-700)",
    mb: 2.5,
  },
  textButton: {
    fontSize: { xs: "16px", md: "20px", xl: "25px" },
  },
  button: {
    width: "287px",
    marginX: "auto",
    marginTop: 7,
  },
};

/**
 * ProductCode
 */
export default function ProductCode() {
  const [productCode, setProductCode] = useState<string>("");
  const [carTireList, setCarTireList] = useState<ITireItemDetail[]>([]);

  const navigate = useNavigate();
  const { tireDetail } = useProductCode(productCode);

  const handleLinkClick = () => {
    if (carTireList.length === 0) {
      Notify.setNotification({
        show: true,
        title: "商品が選択されていません",
      });
      return;
    }
    const firstItem = carTireList[0];
    if (
      carTireList.some(
        (item) =>
          item.tire_width !== firstItem.tire_width ||
          item.tire_aspect_ratio !== firstItem.tire_aspect_ratio ||
          item.tire_rim_diam !== firstItem.tire_rim_diam,
      )
    ) {
      Notify.setNotification({
        show: true,
        title: "同一のタイヤサイズで検索してください",
      });
      return;
    }

    const tires = carTireList.map((item) => ({
      item_cd: item.item_cd,
      tire_size: `${item.tire_width}/${item.tire_aspect_ratio}R${item.tire_rim_diam}`,
      tire_width: item.tire_width,
      tire_rim_diam: item.tire_rim_diam,
      tire_aspect_ratio: item.tire_aspect_ratio,
      tire_category: item.tire_category,
    }));

    sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_FROM, "product-code");
    sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_TIRE, JSON.stringify(tires));
    sessionStorage.removeItem(SESSION_KEY.NEW_QUOTE_MAKER);
    sessionStorage.removeItem(SESSION_KEY.SELECTED_TIRE);
    sessionStorage.removeItem(SESSION_KEY.SELECTED_MAKER);
    sessionStorage.removeItem(SESSION_KEY.SELECTED_CATEGORY);
    sessionStorage.removeItem(SESSION_KEY.SET_TIRE_SELECTED_MAKER);
    sessionStorage.removeItem(SESSION_KEY.SET_TIRE_SELECTED_CATEGORY);
    sessionStorage.removeItem(SESSION_KEY.WHEEL_SELECTED_MAKER);
    sessionStorage.removeItem(SESSION_KEY.WHEEL_SELECTED_CATEGORY);
    navigate(routes.mstProcFee);
  };

  const handleBackToQuoteList = () => navigate(routes.quoteList);

  const handleSearchCarTireByCode = useCallback((searchCode: string): void => {
    setProductCode(searchCode);
  }, []);

  const handleRemoveCarTire = useCallback((id: number): void => {
    setCarTireList((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Effect to handle the addition of tire details to the list
  useEffect(() => {
    if (productCode && !isEmpty(tireDetail) && tireDetail.id) {
      setProductCode("");
      setCarTireList((prev) => {
        const exists = prev.some((item) => item.id === tireDetail.id);
        if (exists) return [...prev];
        return [...prev, tireDetail];
      });
    }
  }, [tireDetail, productCode]);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ padding: 2, p: { sm: 2, lg: 7 }, display: { sm: "block", lg: "flex" } }}>
      <Box>
        <Typography sx={{ ...styles.title }}>新規見積</Typography>
        <BackButton
          sx={{ ...styles.textButton }}
          label="見積一覧"
          onClick={handleBackToQuoteList}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: 26, md: 28, xl: 34 },
            textAlign: "center",
            mt: 4,
            mb: { xs: 4, xl: 8 },
          }}
        >
          商品コードから選ぶ
        </Typography>
        <Box sx={{ overflowX: "auto", width: { xs: "100%", lg: "auto" } }}>
          <TableCarTireByCode
            dataTable={carTireList}
            onRemoveItem={handleRemoveCarTire}
            onSearch={handleSearchCarTireByCode}
          />
        </Box>
        <ButtonComponent
          theme="dark"
          label="商品選択へ進む"
          sx={{ ...styles.button, ...styles.textButton }}
          onClick={handleLinkClick}
        />
      </Box>
    </Box>
    </>
  );
}
